import { apiClient } from './api';

export interface Coupon {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: 'percentage' | 'fixed_amount' | 'free_product';
  value: number;
  maxDiscount?: number;
  minOrderAmount?: number;
  maxUsageCount?: number;
  maxUsagePerCustomer?: number;
  usageCount: number;
  validFrom?: Date;
  validTo?: Date;
  status: 'active' | 'inactive' | 'expired';
  isAutoGenerated: boolean;
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
  restrictions?: CouponRestriction[];
}

export interface CouponRestriction {
  id: string;
  couponId: string;
  type: 'customer_type' | 'product' | 'time' | 'day_of_week';
  value: string;
}

export interface CouponValidationData {
  customerId?: string;
  customerTypeId?: string;
  productCategories: string[];
  productIds?: string[];
  orderAmount: number;
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  time: string; // HH:MM format
  date: Date;
}

export interface CouponValidationResult {
  isValid: boolean;
  coupon?: Coupon;
  discountAmount?: number;
  errorMessage?: string;
}

/**
 * Context-only failures: no red error strip; show a neutral hint instead so Apply doesn’t feel broken.
 */
const COUPON_APPLICABILITY_FAILURE_MESSAGES = new Set([
  'This coupon is not valid for your customer type',
  'This coupon is not valid on this day',
  'This coupon is not valid for this tee time',
  'This coupon is not valid for this booking time',
  'This coupon is not valid for this date',
  'This coupon is not valid for the selected products',
]);

const COUPON_APPLICABILITY_HINT =
  'Try another code, or change player rate, tee time, or day if restrictions apply.';

export type CouponCheckoutValidationPartition = {
  /** Red error banner (invalid coupon, expired, etc.). */
  errorBanner: CouponValidationResult | null;
  /** Neutral copy when the code is real but restrictions rule it out for this booking. */
  applicabilityHint: string | null;
  /** Present only when the coupon should be applied to the cart. */
  appliedPayload: CouponValidationResult | null;
};

export function partitionCouponValidationForCheckoutUi(
  result: CouponValidationResult
): CouponCheckoutValidationPartition {
  if (result.isValid) {
    return {
      errorBanner: null,
      applicabilityHint: null,
      appliedPayload: result,
    };
  }
  const msg = (result.errorMessage || '').trim();
  if (COUPON_APPLICABILITY_FAILURE_MESSAGES.has(msg)) {
    return {
      errorBanner: null,
      applicabilityHint: COUPON_APPLICABILITY_HINT,
      appliedPayload: null,
    };
  }
  if (!msg) {
    return {
      errorBanner: null,
      applicabilityHint: null,
      appliedPayload: null,
    };
  }
  return {
    errorBanner: result,
    applicabilityHint: null,
    appliedPayload: null,
  };
}

function normalizeCouponValidateResponse(raw: unknown): CouponValidationResult | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.isValid === 'boolean') {
    return o as unknown as CouponValidationResult;
  }
  const inner = o.data;
  if (inner != null && typeof inner === 'object' && typeof (inner as CouponValidationResult).isValid === 'boolean') {
    return inner as CouponValidationResult;
  }
  return null;
}

function normalizeActiveCouponsListResponse(raw: unknown): Coupon[] | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (Array.isArray(o.data)) return o.data as Coupon[];
  return null;
}

export interface BookingData {
  teeTimeId?: string;
  customerId?: string;
  customerTypeId?: string;
  totalAmount: number;
  selectedDate: Date;
  selectedTime: string;
  productCategories?: string[];
   productIds?: string[];
}

export class CouponService {
  /**
   * Get all active coupons that are currently valid
   */
  async getAvailableCoupons(): Promise<Coupon[]> {
    try {
      console.log('🔍 CouponService: Fetching available coupons');
      const response = await apiClient.get<{ success: boolean; data: Coupon[] }>('/coupons/active');
      console.log('🔍 CouponService: Received available coupons:', response);
      
      if (response.data) {
        // Filter out expired coupons and those that have reached usage limits
        const now = new Date();
        const availableCoupons = response.data.filter(coupon => {
          // Check if coupon is active
          if (coupon.status !== 'active') return false;
          
          // Check date validity
          if (coupon.validFrom && new Date(coupon.validFrom) > now) return false;
          if (coupon.validTo && new Date(coupon.validTo) < now) return false;
          
          // Check usage limits
          if (coupon.maxUsageCount && coupon.usageCount >= coupon.maxUsageCount) return false;
          
          return true;
        });
        
        return availableCoupons;
      }
      
      return [];
    } catch (error) {
      console.error('🔍 CouponService: Error fetching available coupons:', error);
      return [];
    }
  }

  /**
   * Active coupons that apply to this booking (restrictions, min order, customer type, etc.).
   * Falls back to {@link getAvailableCoupons} if the API does not support the endpoint yet.
   */
  async getAvailableCouponsForBooking(bookingData: BookingData): Promise<Coupon[]> {
    try {
      const validationData: CouponValidationData = {
        customerId: bookingData.customerId,
        customerTypeId: bookingData.customerTypeId,
        productCategories: bookingData.productCategories || ['golf-round'],
        productIds: bookingData.productIds || [],
        orderAmount: bookingData.totalAmount,
        dayOfWeek: bookingData.selectedDate.getDay(),
        time: bookingData.selectedTime,
        date: bookingData.selectedDate,
      };

      const response = await apiClient.post<any>('/coupons/active-for-booking', {
        validationData,
      });

      const list = normalizeActiveCouponsListResponse(response);
      if (!list) {
        console.warn('🔍 CouponService: Unexpected active-for-booking response', response);
        return this.getAvailableCoupons();
      }

      const now = new Date();
      return list.filter((coupon) => {
        if (coupon.status !== 'active') return false;
        if (coupon.validFrom && new Date(coupon.validFrom) > now) return false;
        if (coupon.validTo && new Date(coupon.validTo) < now) return false;
        if (coupon.maxUsageCount != null && coupon.usageCount >= coupon.maxUsageCount) return false;
        return true;
      });
    } catch (error) {
      console.error('🔍 CouponService: active-for-booking failed', error);
      // Do not fall back to getAvailableCoupons here — that doubles request volume and
      // was contributing to browser ERR_INSUFFICIENT_RESOURCES during checkout re-renders.
      return [];
    }
  }

  /**
   * Validate a coupon code with booking context
   */
  async validateCoupon(code: string, bookingData: BookingData): Promise<CouponValidationResult> {
    try {
      console.log('🔍 CouponService: Validating coupon:', code, bookingData);
      
      const validationData: CouponValidationData = {
        customerId: bookingData.customerId,
        customerTypeId: bookingData.customerTypeId,
        productCategories: bookingData.productCategories || ['golf-round'], // Default to golf rounds for tee time bookings
        productIds: bookingData.productIds || [],
        orderAmount: bookingData.totalAmount,
        dayOfWeek: bookingData.selectedDate.getDay(),
        time: bookingData.selectedTime,
        date: bookingData.selectedDate
      };

      // Backend wraps result as { success: boolean; data: CouponValidationResult }
      const response = await apiClient.post<any>('/coupons/validate', {
        code,
        validationData
      });

      const payload = normalizeCouponValidateResponse(response);
      if (!payload || typeof payload.isValid !== 'boolean') {
        console.warn('🔍 CouponService: Unexpected validate response shape', response);
        return {
          isValid: false,
          errorMessage: 'Could not read coupon response. Please try again.',
        };
      }
      console.log('🔍 CouponService: Coupon validation result (normalized):', payload);
      return payload;
    } catch (error) {
      console.error('🔍 CouponService: Error validating coupon:', error);
      return {
        isValid: false,
        errorMessage: error instanceof Error ? error.message : 'Failed to validate coupon'
      };
    }
  }

  /**
   * Apply a coupon to a booking
   */
  async applyCoupon(code: string, bookingData: BookingData): Promise<CouponValidationResult> {
    try {
      console.log('🔍 CouponService: Applying coupon:', code, bookingData);
      
      const validationData: CouponValidationData = {
        customerId: bookingData.customerId,
        customerTypeId: bookingData.customerTypeId,
        productCategories: bookingData.productCategories || ['golf-round'],
        productIds: bookingData.productIds || [],
        orderAmount: bookingData.totalAmount,
        dayOfWeek: bookingData.selectedDate.getDay(),
        time: bookingData.selectedTime,
        date: bookingData.selectedDate
      };

      const response = await apiClient.post<any>('/coupons/apply', {
        code,
        validationData
      });

      const payload: CouponValidationResult = response?.data ?? response;
      console.log('🔍 CouponService: Coupon application result (normalized):', payload);
      return payload;
    } catch (error) {
      console.error('🔍 CouponService: Error applying coupon:', error);
      return {
        isValid: false,
        errorMessage: error instanceof Error ? error.message : 'Failed to apply coupon'
      };
    }
  }
}

export const couponService = new CouponService();
